/**
 * Goose settings + Goose cache status, shared by the Day Ahead and Complete Backtesting tabs.
 *
 * The day-ahead side of both tabs is the IDA1-Goose strategy for the settings chosen here (same
 * fields and defaults as the IDA1-Goose page). Its results are cached per day on the server. There
 * is no build button: Run calls `ensure(start, end)`, which asks the backend to compute only the
 * days of the window that are not cached yet (from the Goose site) and waits until they are in.
 *
 * Backend: POST /dayahead/goose/status {settings, start, end} (read-only) and
 *          POST /dayahead/goose/ensure {settings, start, end} (computes what is missing).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";

export const detailText = (d, fallback) => (typeof d === "string" ? d : d?.message || fallback);

// Labels as on the IDA1-Goose page.
const LABEL = {
  probabilistic_model: "Probabilistic Model",
  fix_i_model: "Fix I Model",
  fix_ii_model: "Fix II Model",
  ida1_model: "IDA1 Model",
  fix_i_model_spread: "Fix I Model Spread",
  base_volume: "Base Volume",
  ida1_margin: "IDA1 Margin",
  prediction_filter_margin: "Trend Filter Margin (PLN)",
  bucket1_volume: "Fix I vs Fix II Bucket 1 Volume",
  bucket2_volume: "Fix I vs Fix II Bucket 2 Volume",
  bucket3_volume: "Fix I vs Fix II Bucket 3 Volume",
  fix_ii_ida1_bucket1_volume: "Fix II vs IDA1 Bucket 1 Volume",
  fix_ii_ida1_bucket2_volume: "Fix II vs IDA1 Bucket 2 Volume",
  fix_ii_ida1_bucket3_volume: "Fix II vs IDA1 Bucket 3 Volume",
  max_avg_volume_pct: "Max IDA1 Avg Volume (%)",
  max_position: "Max IDA1 Position (MW)",
  margin_tier1: "Margin: Price < 500",
  margin_tier2: "Margin: Price 500-700",
  margin_tier3: "Margin: Price 700-1000",
  margin_tier4: "Margin: Price > 1000",
  np_constraint_bucket1: "Bucket 1 NP Constraint (MWh)",
  np_constraint_bucket2: "Bucket 2 NP Constraint (MWh)",
  np_constraint_bucket3: "Bucket 3 NP Constraint (MWh)",
  risk_score_model: "Price Collapse Risk Engine",
  spike_risk_model: "Price Spike Risk Engine",
};
const MAIN = ["probabilistic_model", "prediction_filter_margin", "fix_i_model_spread", "base_volume"];
const MORE = [
  ["fix_i_model", "fix_ii_model", "ida1_model", "ida1_margin"],
  ["bucket1_volume", "bucket2_volume", "bucket3_volume"],
  ["fix_ii_ida1_bucket1_volume", "fix_ii_ida1_bucket2_volume", "fix_ii_ida1_bucket3_volume"],
  ["max_avg_volume_pct", "max_position"],
  ["margin_tier1", "margin_tier2", "margin_tier3", "margin_tier4"],
  ["np_constraint_bucket1", "np_constraint_bucket2", "np_constraint_bucket3"],
];
const STEP = { fix_i_model_spread: 0.1, max_avg_volume_pct: 5, max_position: 10, margin_tier1: 10, margin_tier2: 10, margin_tier3: 10, margin_tier4: 10 };
const POLL_MS = 2000;
const MAX_WAIT_MS = 15 * 60 * 1000;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Goose settings state, the cache status of [start, end], and `ensure` for Run. */
export function useGooseBook(gooseMeta, fetchPost, start, end) {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => {
    alive.current = false;
  }, []);

  useEffect(() => {
    if (gooseMeta && !settings) setSettings({ ...gooseMeta.defaults });
  }, [gooseMeta, settings]);

  const post = useCallback(
    async (path, s) => {
      const res = await fetchPost(path, { settings: s, start, end });
      const data = await res.json();
      if (!res.ok || data.status !== "success") throw new Error(detailText(data.detail, "Goose cache request failed."));
      return data;
    },
    [fetchPost, start, end]
  );

  // what is cached for the current settings and dates (read-only, debounced)
  useEffect(() => {
    if (!settings || !start || !end) return undefined;
    let on = true;
    const t = setTimeout(async () => {
      try {
        const st = await post("/dayahead/goose/status", settings);
        if (on) {
          setStatus(st);
          setError(null);
        }
      } catch (e) {
        if (on) setError(e.message);
      }
    }, 300);
    return () => {
      on = false;
      clearTimeout(t);
    };
  }, [settings, start, end, post]);

  const setSetting = useCallback((k, v) => {
    setStatus(null);
    setSettings((s) => ({ ...s, [k]: v }));
  }, []);

  const reset = useCallback(() => {
    if (!gooseMeta) return;
    setStatus(null);
    setSettings({ ...gooseMeta.defaults });
  }, [gooseMeta]);

  /** Make every day of [start, end] cached (computing the missing ones); resolves to the book key. */
  const ensure = useCallback(async () => {
    setError(null);
    const t0 = Date.now();
    for (;;) {
      const st = await post("/dayahead/goose/ensure", settings);
      if (!alive.current) throw new Error("cancelled");
      setStatus(st);
      if (st.state === "ready") return st.key;
      if (st.state === "error") throw new Error(`Goose computation failed: ${st.error || "unknown error"}`);
      if (Date.now() - t0 > MAX_WAIT_MS) throw new Error("The Goose computation is taking too long; press Run again later.");
      await sleep(POLL_MS);
    }
  }, [post, settings]);

  return { settings, setSetting, reset, status, error, setError, ensure };
}

const fmtInt = (v) => (v == null || Number.isNaN(Number(v)) ? "–" : Math.round(Number(v)).toLocaleString("en-US"));

/** One line: how much of the selected window is cached, or the progress of the computation. */
export const GooseCacheStatus = ({ goose, gooseTotal, dark }) => {
  const st = goose.status;
  const muted = dark ? "text-slate-400" : "text-slate-500";
  let text;
  let tone = muted;
  if (!st) text = "Checking the Goose cache…";
  else if (st.state === "ready") {
    text = `Goose results cached for all ${fmtInt(st.days_total)} days of the selected dates${gooseTotal != null ? ` · Goose ${fmtInt(gooseTotal)} PLN` : ""}.`;
    tone = dark ? "text-emerald-400" : "text-emerald-700";
  } else if (st.state === "computing") {
    const p = st.progress || {};
    text = `Computing Goose results on the Goose site… ${fmtInt(p.days_done)} of ${fmtInt(p.days_todo)} days${p.range ? ` (${p.range})` : ""}. The Goose site keeps serving orders meanwhile.`;
  } else if (st.state === "queued") {
    text = "Another Goose computation is running on the server; this one starts as soon as it finishes.";
  } else if (st.state === "error") {
    text = `The last Goose computation failed: ${st.error || "unknown error"}. Press Run to try again.`;
    tone = "text-rose-600";
  } else {
    text = `${fmtInt(st.days_cached)} of ${fmtInt(st.days_total)} days cached for these Goose settings; Run computes the other ${fmtInt(st.days_missing)} on the Goose site first.`;
  }
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className={`text-xs ${tone}`} data-testid="goose-status">{text}</span>
      {goose.error ? <span className="text-xs text-rose-600">{goose.error}</span> : null}
    </div>
  );
};

/** The Goose settings form (IDA1-Goose page fields) with the cache status underneath. */
const GooseBookPanel = ({ gooseMeta, goose, gooseTotal, dark, inputCls, labelCls }) => {
  const [more, setMore] = useState(false);
  const s = goose.settings;
  if (!gooseMeta || !s) return null;
  const muted = dark ? "text-slate-400" : "text-slate-500";
  const faint = dark ? "text-slate-500" : "text-slate-400";
  const dflt = gooseMeta.defaults || {};
  const opts = gooseMeta.options || {};

  const field = (k) => {
    const v = s[k];
    if (typeof dflt[k] === "string") {
      const choices = opts[k] || [];
      const list = choices.includes(v) ? choices : [v, ...choices];
      return (
        <div key={k}>
          <label className={labelCls}>{LABEL[k] || k}</label>
          <select className={inputCls} value={v ?? ""} onChange={(e) => goose.setSetting(k, e.target.value)} aria-label={LABEL[k] || k}>
            {list.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
      );
    }
    return (
      <div key={k}>
        <label className={labelCls}>{LABEL[k] || k}</label>
        <input
          type="number"
          min="0"
          step={STEP[k] || 1}
          className={inputCls}
          value={v ?? ""}
          aria-label={LABEL[k] || k}
          onChange={(e) => goose.setSetting(k, e.target.value === "" ? "" : Number(e.target.value))}
        />
      </div>
    );
  };
  // Shift fields: checkbox + one-line label, the same height as the other labels so the selects line up.
  const shift = (onKey, modelKey, label, tip, color) => (
    <div>
      <label className={`flex items-center gap-1.5 h-4 mb-1 text-xs font-medium whitespace-nowrap overflow-hidden ${muted}`} title={tip}>
        <input type="checkbox" className="h-3.5 w-3.5 shrink-0" checked={!!s[onKey]} onChange={(e) => goose.setSetting(onKey, e.target.checked)} />
        <span className={`truncate ${color}`}>{label}</span>
      </label>
      <select
        className={inputCls}
        value={s[modelKey] ?? ""}
        disabled={!s[onKey]}
        aria-label={LABEL[modelKey]}
        onChange={(e) => goose.setSetting(modelKey, e.target.value)}
      >
        {(opts[modelKey] || [s[modelKey]]).map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className={`rounded-xl border p-3 space-y-3 ${dark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className={`text-xs font-semibold uppercase tracking-wide ${dark ? "text-slate-300" : "text-slate-600"}`}>
          Day-ahead model (IDA1-Goose)
        </div>
        <div className={`text-[11px] ${faint}`}>Same fields and defaults as the IDA1-Goose page. Results are cached per day and setting.</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
        {MAIN.map(field)}
        {shift("apply_risk_score", "risk_score_model", "Price Collapse Risk (down shift)",
          "Apply Price Collapse Risk: lowers the traded quantiles when the price-collapse risk score is high.", "text-emerald-600")}
        {shift("apply_spike_risk", "spike_risk_model", "Price Spike Risk (up shift)",
          "Apply Price Spike Risk: raises the traded quantiles when a price spike is likely.", "text-amber-600")}
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <label className={`flex items-center gap-2 text-xs ${muted}`}>
          <input type="checkbox" checked={!!s.apply_ridge_shift} onChange={(e) => goose.setSetting("apply_ridge_shift", e.target.checked)} />
          Apply Ridge Shift
        </label>
        <button type="button" className={`text-xs underline ${muted}`} onClick={() => setMore((m) => !m)}>
          {more ? "Hide" : "More Goose settings"} (models, IDA1 margin, buckets, Fix II margins, NP constraints)
        </button>
        <button type="button" className={`text-xs underline ${muted}`} onClick={goose.reset}>Goose defaults</button>
      </div>
      {more ? (
        <div className="space-y-3">
          {MORE.map((row, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              {row.map(field)}
            </div>
          ))}
        </div>
      ) : null}
      <GooseCacheStatus goose={goose} gooseTotal={gooseTotal} dark={dark} />
    </div>
  );
};

export default GooseBookPanel;
